import { useBreakPoints } from '@/hooks/useBreakPoints';
import dayjs from '@/init/dayjs';
import { pagesPath } from '@/lib/$path';
import { getPostCode } from '@/helpers/postCodeHelper';
import { getPrefName } from '@/data/prefectures';
import {
  api_portalOfficeListing_filter,
  ibow2_proto_PortalOfficeListing,
} from '@/init/swagger';
import { CardActionArea } from '@mui/material';
import { ReactNode } from 'react';
import { useSearchParams } from 'next/navigation';
import { assortServiceItems } from '@/helpers/serviceCategory';

type CardWrapperProps = {
  href: string;
  children: ReactNode;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

export const CardWrapper: React.FC<CardWrapperProps> = ({
  href,
  children,
  onMouseEnter,
  onMouseLeave,
}) => {
  return (
    <CardActionArea
      href={href}
      target="_blank"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </CardActionArea>
  );
};

type LogicProps = {
  listing: ibow2_proto_PortalOfficeListing;
  searchFilters: api_portalOfficeListing_filter[];
};

export const useLogic = ({ listing, searchFilters }: LogicProps) => {
  const { isMobile } = useBreakPoints();
  const searchParams = useSearchParams();

  const { services: serviceCategoryItems } = assortServiceItems({
    items: listing.portal_office_listing_services,
    masterItems: searchFilters,
  });
  const hasServiceCategoryItems =
    serviceCategoryItems && serviceCategoryItems.length > 0;

  const href = `${
    pagesPath.public.stations
      ._portalOfficeId(listing.portal_office_id || '')
      .$url().path
  }?${searchParams.toString()}`;

  const postCode = listing.portal_office?.post_code
    ? `〒${getPostCode(listing.portal_office?.post_code)}`
    : '';

  const address = [
    getPrefName(listing.portal_office?.pref || 0),
    listing.portal_office?.city,
    listing.portal_office?.area,
    listing.portal_office?.building || '',
  ].join('');

  const publishedOnStr = listing.portal_office?.listing_released_at
    ? dayjs(listing.portal_office?.listing_released_at).format('YY.M.D')
    : '-';
  const updatedOnStr = listing.portal_office?.listing_updated_at
    ? dayjs(listing.portal_office?.listing_updated_at).format('YY.M.D')
    : '-';

  const onClickRequest: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();

    window.open(
      pagesPath.app.requests.create
        ._portalOfficeId(listing?.portal_office_id as number)
        .$url().path,
      '_blank',
    );
  };

  const isDxStation = !!listing.portal_office?.ibow_office_id;
  const isCarelogood = listing.portal_office?.is_carelogood === true;

  const tel =
    listing.portal_office?.tel_area &&
    listing.portal_office?.tel_city &&
    listing.portal_office?.tel_num
      ? `${listing.portal_office.tel_area}-${listing.portal_office.tel_city}-${listing.portal_office.tel_num}`
      : undefined;

  return {
    isMobile,
    postCode,
    address,
    publishedOnStr,
    updatedOnStr,
    onClickRequest,
    href,
    isDxStation,
    isCarelogood,
    tel,
    hasServiceCategoryItems,
    serviceCategoryItems,
  };
};
